import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './VoiceprintAdmin.module.css';
import { createVoiceprintCapture } from '../../services/voiceprint-capture';

const MAX_RECORD_MS = 20000;

export default function VoiceprintAdmin() {
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [filter, setFilter] = useState('all');  // 'all' | 'enrolled' | 'missing'
    const [search, setSearch] = useState('');
    const [activeStudent, setActiveStudent] = useState(null);
    const [error, setError] = useState(null);
    const [me, setMe] = useState(null);

    useEffect(() => {
        fetch('/api/v1/me').then(r => r.ok ? r.json() : null).then(setMe);
        refresh();
    }, []);

    const refresh = async () => {
        try {
            const res = await fetch('/api/v1/students');
            if (!res.ok) {
                setError(`Could not load students: HTTP ${res.status}`);
                return;
            }
            setStudents(await res.json());
        } catch (e) {
            setError('Network error: ' + (e.message || e));
        }
    };

    const filtered = students.filter(s => {
        if (filter === 'enrolled' && !s.has_voiceprint) return false;
        if (filter === 'missing' && s.has_voiceprint) return false;
        if (search) {
            const q = search.toLowerCase();
            if (!s.email.toLowerCase().includes(q) &&
                !(s.display_name || '').toLowerCase().includes(q)) return false;
        }
        return true;
    });

    const isAdmin = me && (me.role === 'admin' || me.role === 'super');

    if (me && !isAdmin) {
        return (
            <div className={styles.page}>
                <div className={styles.title}>Voiceprint Management</div>
                <div className={styles.error}>This page is admin-only.</div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <span className={styles.backLink} onClick={() => navigate('/home')}>← Back to Home</span>
            <h1 className={styles.title}>Voiceprint Management</h1>
            <p className={styles.subtitle}>
                Record or replace any student's voice fingerprint on this device.
                Useful when a student's own laptop or phone has trouble capturing audio cleanly.
            </p>

            <div className={styles.toolbar}>
                <input
                    className={styles.searchInput}
                    placeholder="Search by login id or display name…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <button
                    className={`${styles.filterPill} ${filter === 'all' ? styles.filterPillActive : ''}`}
                    onClick={() => setFilter('all')}
                >All ({students.length})</button>
                <button
                    className={`${styles.filterPill} ${filter === 'enrolled' ? styles.filterPillActive : ''}`}
                    onClick={() => setFilter('enrolled')}
                >Enrolled ({students.filter(s => s.has_voiceprint).length})</button>
                <button
                    className={`${styles.filterPill} ${filter === 'missing' ? styles.filterPillActive : ''}`}
                    onClick={() => setFilter('missing')}
                >Missing ({students.filter(s => !s.has_voiceprint).length})</button>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.list}>
                {filtered.map(s => (
                    <div key={s.id} className={styles.row}>
                        <div className={styles.id}>{s.email}</div>
                        <div className={styles.name}>{s.display_name || <em style={{color:'#9ca3af'}}>no name set</em>}</div>
                        <div>
                            {s.has_voiceprint ? (
                                <span className={styles.statusOk}>● Enrolled</span>
                            ) : (
                                <span className={styles.statusEmpty}>○ Not recorded</span>
                            )}
                        </div>
                        <div className={styles.actions}>
                            {s.has_voiceprint && (
                                <button className={styles.btn} onClick={() => {
                                    const a = new Audio(`/api/v1/admin/users/${s.id}/voiceprint/audio?t=${Date.now()}`);
                                    a.play().catch(() => {});
                                }}>▶ Play</button>
                            )}
                            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setActiveStudent(s)}>
                                {s.has_voiceprint ? 'Re-record' : 'Record'}
                            </button>
                            {s.has_voiceprint && (
                                <button className={`${styles.btn} ${styles.btnDanger}`} onClick={async () => {
                                    if (!window.confirm(`Delete ${s.email}'s voiceprint?`)) return;
                                    const res = await fetch(`/api/v1/admin/users/${s.id}/voiceprint`, { method: 'DELETE' });
                                    if (res.ok) refresh();
                                }}>Delete</button>
                            )}
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && <div className={styles.muted}>No students match the filter.</div>}
            </div>

            {activeStudent && (
                <RecordPanel
                    student={activeStudent}
                    onClose={() => { setActiveStudent(null); refresh(); }}
                />
            )}
        </div>
    );
}

function RecordPanel({ student, onClose }) {
    const [recording, setRecording] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [elapsedMs, setElapsedMs] = useState(0);
    const recordRef = useRef({ cap: null, timer: null, tickTimer: null });

    async function start() {
        setError(null); setSuccess(null);
        try {
            const cap = createVoiceprintCapture();
            await cap.start();
            const t0 = Date.now();
            const tickTimer = setInterval(() => setElapsedMs(Date.now() - t0), 100);
            recordRef.current = { cap, tickTimer, timer: null };
            setRecording(true);
            setElapsedMs(0);
            recordRef.current.timer = setTimeout(() => stop(), MAX_RECORD_MS);
        } catch (e) {
            setError('Microphone access denied or unavailable: ' + (e.message || e));
        }
    }

    async function stop() {
        const ref = recordRef.current;
        if (ref.timer) clearTimeout(ref.timer);
        if (ref.tickTimer) clearInterval(ref.tickTimer);
        const merged = ref.cap ? ref.cap.stop() : new Float32Array(0);
        setRecording(false);

        const total = merged.length;
        if (total < 16000) {
            setError('Recording too short — at least 1 second is required.');
            return;
        }

        setUploading(true);
        try {
            const res = await fetch(`/api/v1/admin/users/${student.id}/voiceprint`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'X-Audio-Encoding': 'pcm_f32le',
                    'X-Audio-Sample-Rate': '16000',
                    'X-Audio-Duration-Ms': String(Math.round(total / 16)),
                },
                body: merged.buffer,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                setError(err.message || 'Upload failed.');
            } else {
                setSuccess(`Saved voiceprint for ${student.email} (${(total / 16000).toFixed(1)}s).`);
            }
        } catch (e) {
            setError('Upload failed: ' + (e.message || e));
        } finally {
            setUploading(false);
        }
    }

    return (
        <div className={styles.recorder}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <div style={{ fontWeight: 600 }}>Recording for: <span className={styles.id}>{student.email}</span> {student.display_name && <span className={styles.muted}>({student.display_name})</span>}</div>
                    <div className={styles.muted}>Aim for about 20 seconds of natural speech in a quiet space.</div>
                </div>
                <button className={styles.btn} onClick={onClose}>Close</button>
            </div>
            <div style={{ marginTop: 12 }}>
                {recording ? (
                    <button className={`${styles.recordButton} ${styles.recording}`} onClick={stop}>
                        <span className={styles.dot} /> Stop &amp; Save  &nbsp; {(elapsedMs / 1000).toFixed(1)}s
                    </button>
                ) : (
                    <button className={styles.recordButton} onClick={start} disabled={uploading}>
                        {uploading ? 'Uploading…' : 'Start recording'}
                    </button>
                )}
            </div>
            {error && <div className={styles.error}>{error}</div>}
            {success && <div className={styles.success}>{success}</div>}
        </div>
    );
}
